import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc, arrayUnion, arrayRemove, getDocs, getDoc, setDoc, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase/config';
import { ScheduledMeeting, ZoomSettings } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../firebase/config'; // Added for auth state verification

interface UseZoomMeetingsProps {
	classroomId: string;
}

export const useZoomMeetings = ({ classroomId }: UseZoomMeetingsProps) => {
	const { currentUser } = useAuth();
	const [meetings, setMeetings] = useState<ScheduledMeeting[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [zoomSettings, setZoomSettings] = useState<ZoomSettings | null>(null);

	// Load meetings for the classroom
	const loadMeetings = useCallback(() => {
		if (!classroomId) return;
		
		setLoading(true);
		setError(null);
		
		try {
			// Subscribe to meetings changes (shared across members)
			const meetingsRef = collection(db, 'classrooms', classroomId, 'meetings');
			const legacyMeetingsRef = collection(db, 'classrooms', classroomId, 'meetingns');
			const q = query(meetingsRef, orderBy('startTime', 'asc'), limit(100));
			const qLegacy = query(legacyMeetingsRef, orderBy('startTime', 'asc'), limit(100));

			let latestPrimary: ScheduledMeeting[] = [];
			let latestLegacy: ScheduledMeeting[] = [];

			const mergeAndSet = () => {
				const byId = new Map<string, ScheduledMeeting>();
				[...latestPrimary, ...latestLegacy].forEach((m) => {
					byId.set(m.id, m);
				});
				const merged = Array.from(byId.values()).sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
				setMeetings(merged);
			};

			const unsubMeetings = onSnapshot(q, (snap) => {
				const loaded: ScheduledMeeting[] = [];
				snap.forEach((mDoc) => {
					const data = mDoc.data() as any;
					const rawStart = data.startTime;
					const normalizedStart = rawStart?.toDate ? rawStart.toDate().toISOString() : (typeof rawStart === 'string' ? rawStart : new Date(rawStart).toISOString());
					loaded.push({ ...data, id: mDoc.id, startTime: normalizedStart });
				});
				latestPrimary = loaded;
				mergeAndSet();
			});

			const unsubLegacy = onSnapshot(qLegacy, (snap) => {
				const loaded: ScheduledMeeting[] = [];
				snap.forEach((mDoc) => {
					const data = mDoc.data() as any;
					const rawStart = data.startTime;
					const normalizedStart = rawStart?.toDate ? rawStart.toDate().toISOString() : (typeof rawStart === 'string' ? rawStart : new Date(rawStart).toISOString());
					loaded.push({ ...data, id: mDoc.id, startTime: normalizedStart });
				});
				latestLegacy = loaded;
				mergeAndSet();
			});

			// Return cleanup for this subscription set
			return () => {
				try { unsubMeetings(); } catch {}
				try { unsubLegacy(); } catch {}
			};
		} catch (err) {
			setError('Failed to load meetings');
		} finally {
			setLoading(false);
		}
	}, [classroomId]);

	// Create a new Zoom meeting
	const createMeeting = useCallback(async (meetingData: any) => {
		if (!currentUser) {
			setError('You must be signed in to create a meeting.');
			throw new Error('Not authenticated');
		}
		if (!classroomId) {
			setError('No classroom selected.');
			throw new Error('Missing classroomId');
		}

		try {
			// Ensure user is authenticated and auth state is loaded
			if (!currentUser?.uid) {
				console.error('User not authenticated when trying to create meeting');
				setError('You must be signed in to create meetings');
				return null;
			}

			// Check if Firebase Auth is ready
			const authState = auth.currentUser;
			if (!authState) {
				console.error('Firebase Auth not ready, waiting...');
				setError('Authentication not ready. Please wait a moment and try again.');
				return null;
			}

			// Force token refresh to ensure valid authentication
			try {
				console.log('Refreshing authentication token for create meeting...');
				const token = await authState.getIdToken(true);
				console.log('Token refreshed successfully for create meeting:', token ? 'Token exists' : 'No token');
			} catch (tokenError) {
				console.error('Failed to refresh token for create meeting:', tokenError);
				setError('Authentication token expired. Please sign in again.');
				return null;
			}

			console.log('Creating Zoom meeting with data:', meetingData);
			const createZoomMeeting = httpsCallable(functions, 'createZoomMeeting');
			
			const result = await createZoomMeeting({
				topic: meetingData.topic || 'Live Session',
				startTime: meetingData.startTime,
				duration: meetingData.duration || 60,
				classroomId: classroomId,
				hostId: currentUser.uid
			});

			const data = result.data as any;
			console.log('Zoom meeting created successfully:', data);
			
			if (data.success) {
				return data.meeting;
			} else {
				throw new Error(data.error || 'Failed to create meeting');
			}
		} catch (err: any) {
			console.error('Error creating meeting:', err);
			setError(err.message || 'Failed to create meeting');
			throw err;
		}
	}, [currentUser, classroomId]);

	// Join a meeting
	const joinMeeting = useCallback(async (meeting: ScheduledMeeting) => {
		try {
			// Ensure user is authenticated and auth state is loaded
			if (!currentUser?.uid) {
				console.error('User not authenticated when trying to join meeting');
				setError('You must be signed in to join meetings');
				return null;
			}

			// Check if Firebase Auth is ready
			const authState = auth.currentUser;
			if (!authState) {
				console.error('Firebase Auth not ready, waiting...');
				setError('Authentication not ready. Please wait a moment and try again.');
				return null;
			}

			// Force token refresh to ensure valid authentication
			try {
				console.log('Refreshing authentication token...');
				const token = await authState.getIdToken(true);
				console.log('Token refreshed successfully:', token ? 'Token exists' : 'No token');
			} catch (tokenError) {
				console.error('Failed to refresh token:', tokenError);
				setError('Authentication token expired. Please sign in again.');
				return null;
			}

			console.log('Joining Zoom meeting:', meeting.id);
			const joinZoomMeeting = httpsCallable(functions, 'joinZoomMeeting');
			
			const result = await joinZoomMeeting({
				meetingId: meeting.id,
				classroomId: classroomId,
				userId: currentUser.uid
			});

			const data = result.data as any;
			console.log('Zoom meeting join result:', data);
			
			if (data.success) {
				return data.meeting.joinUrl;
			} else {
				throw new Error(data.error || 'Failed to join meeting');
			}
		} catch (err: any) {
			console.error('Error joining meeting:', err);
			setError(err.message || 'Failed to join meeting');
			return null;
		}
	}, [currentUser, classroomId]);

	// Start a meeting (host only)
	const startMeeting = useCallback(async (meeting: ScheduledMeeting) => {
		if (!currentUser || meeting.hostId !== currentUser.uid) {
			setError('Only the meeting host can start the meeting');
			return false;
		}

		try {
			// Ensure user is authenticated and auth state is loaded
			if (!currentUser?.uid) {
				console.error('User not authenticated when trying to start meeting');
				setError('You must be signed in to start meetings');
				return false;
			}

			// Check if Firebase Auth is ready
			const authState = auth.currentUser;
			if (!authState) {
				console.error('Firebase Auth not ready for start meeting, waiting...');
				setError('Authentication not ready. Please wait a moment and try again.');
				return false;
			}

			// Force token refresh to ensure valid authentication
			try {
				console.log('Refreshing authentication token for start meeting...');
				const token = await authState.getIdToken(true);
				console.log('Token refreshed successfully for start meeting:', token ? 'Token exists' : 'No token');
			} catch (tokenError) {
				console.error('Failed to refresh token for start meeting:', tokenError);
				setError('Authentication token expired. Please sign in again.');
				return false;
			}

			console.log('Starting Zoom meeting:', meeting.id);
			const startZoomMeeting = httpsCallable(functions, 'startZoomMeeting');
			
			const result = await startZoomMeeting({
				meetingId: meeting.id,
				classroomId: classroomId,
				userId: currentUser.uid
			});

			const data = result.data as any;
			console.log('Zoom meeting start result:', data);
			
			if (data.success) {
				return data.meeting.startUrl;
			} else {
				throw new Error(data.error || 'Failed to start meeting');
			}
		} catch (err: any) {
			console.error('Error starting meeting:', err);
			setError(err.message || 'Failed to start meeting');
			return false;
		}
	}, [currentUser, classroomId]);

	// End a meeting (host/admin)
	const endMeeting = useCallback(async (meeting: ScheduledMeeting) => {
		if (!currentUser?.uid) {
			setError('You must be signed in to end the meeting');
			return false;
		}
		try {
			const callable = httpsCallable(functions, 'endZoomMeeting');
			const result = await callable({ meetingId: meeting.id, classroomId, userId: currentUser.uid });
			const data = result.data as any;
			return !!data?.success;
		} catch (err: any) {
			console.error('Error ending meeting:', err);
			setError(err.message || 'Failed to end meeting');
			return false;
		}
	}, [currentUser, classroomId]);

	// Get meeting details
	const getMeetingDetails = useCallback(async (meetingId: string) => {
		if (!currentUser?.uid) {
			setError('You must be signed in to get meeting details');
			return null;
		}

		try {
			const getZoomMeetingDetails = httpsCallable(functions, 'getZoomMeetingDetails');
			
			const result = await getZoomMeetingDetails({
				meetingId: meetingId,
				classroomId: classroomId,
				userId: currentUser.uid
			});

			const data = result.data as any;
			if (data.success) {
				return data.meeting;
			} else {
				throw new Error(data.error || 'Failed to get meeting details');
			}
		} catch (err: any) {
			console.error('Error getting meeting details:', err);
			setError(err.message || 'Failed to get meeting details');
			return null;
		}
	}, [currentUser, classroomId]);

	// Fetch recording link and announce
	const fetchRecordingLink = useCallback(async (meetingId: string) => {
		if (!currentUser?.uid) {
			setError('You must be signed in to fetch recording');
			return null;
		}
		try {
			const callable = httpsCallable(functions, 'fetchZoomRecording');
			const result = await callable({ meetingId, classroomId, userId: currentUser.uid });
			const data = result.data as any;
			return data?.shareUrl || null;
		} catch (err: any) {
			console.error('Error fetching recording link:', err);
			setError(err.message || 'Failed to fetch recording');
			return null;
		}
	}, [currentUser, classroomId]);

	// Update meeting
	const updateMeeting = useCallback(async (meetingId: string, updates: any) => {
		try {
			const meetingRef = doc(db, 'classrooms', classroomId, 'meetings', meetingId);
			await updateDoc(meetingRef, {
				...updates,
				updatedAt: new Date().toISOString()
			});
		} catch (err) {
			setError('Failed to update meeting');
			throw err;
		}
	}, [classroomId]);

	// Delete meeting
	const deleteMeeting = useCallback(async (meetingId: string) => {
		try {
			const meetingRef = doc(db, 'classrooms', classroomId, 'meetings', meetingId);
			await deleteDoc(meetingRef);
		} catch (err) {
			setError('Failed to delete meeting');
			throw err;
		}
	}, [classroomId]);

	// Load meetings on mount
	useEffect(() => {
		const cleanup = loadMeetings();
		return () => {
			if (typeof cleanup === 'function') cleanup();
		};
	}, [loadMeetings]);

	return {
		meetings,
		loading,
		error,
		createMeeting,
		joinMeeting,
		startMeeting,
		endMeeting,
		getMeetingDetails,
		fetchRecordingLink,
		updateMeeting,
		deleteMeeting,
		setError
	};
};


